import React, { useCallback } from 'react'
import { Box, ButtonBase, Stack, Typography, useMediaQuery } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  CORAL_600,
  GRAY_400,
  GRAY_500,
  GRAY_900,
  GREEN_600,
  MOBILE_VIEW_WIDTH_QUERY,
  WHITE
} from 'constants/styling/theme'
import type { PfStep } from 'models/purchaseFlowRevamp'

import { useResolvedStepFlow } from '../routing/useResolvedStepFlow'
import { PfStepPath, usePfPath } from '../routing/pfBasePath'
import { PfRevampEventName, usePfRevampAnalytics } from '../analytics'
import { usePfStore } from '../store'

/**
 * PfProgressStepper — guided-tunnel progress indicator for the PF revamp.
 *
 * Consumes `useResolvedStepFlow()` so it renders ONLY the skip-aware steps
 * (a flow with no configurable items shows 4 steps, not 5) and stays in sync
 * with the actual navigation targets.
 *
 * Two variants, switched on `MOBILE_VIEW_WIDTH_QUERY`:
 *   - Desktop (md+): a horizontal labelled stepper — numbered/check badge +
 *     short label, with a connector line between consecutive steps. COMPLETED
 *     steps are clickable to jump back to them (the desktop sidebar owns the
 *     one-step Back; the stepper adds multi-step jump-back).
 *   - Mobile (<=800px): a slim segmented progress bar only (one segment per step,
 *     filled up to the current step); the step name and "Step X of N" are its
 *     accessible label, not shown, so the header stays short. Back navigation on mobile
 *     is handled by the fixed footer's Back arrow, so the slim segments are
 *     informational (too small to be reliable tap targets).
 *
 * It is mounted by `StepShell` inside the sticky top zone, directly beneath the
 * topbar, on both viewports. Its row height is fixed to `PF_STEPPER_HEIGHT`
 * (`PF_STEPPER_HEIGHT_MOBILE` on mobile, see `stickyOffsets.ts`) so the sticky offset of everything below it is exact.
 */

type StepStatus = 'completed' | 'active' | 'upcoming'

const STEP_LABEL_KEY: Record<PfStep, string> = {
  catalogue: 'purchase_flow_revamp:stepper.catalogue',
  configuration: 'purchase_flow_revamp:stepper.configuration',
  instructions: 'purchase_flow_revamp:stepper.instructions',
  billing: 'purchase_flow_revamp:stepper.billing',
  summary: 'purchase_flow_revamp:stepper.summary',
}

const statusFor = (index: number, currentIndex: number): StepStatus => {
  if (index < currentIndex) return 'completed'
  if (index === currentIndex) return 'active'
  return 'upcoming'
}

interface Props {
  /**
   * Desktop layout placement (ignored on mobile, which always renders the slim
   * segmented bar):
   *   - 'band'   : full-width row on its own line below the top bar; connectors
   *                stretch (`flex: 1`). The roomier default.
   *   - 'inline' : compact intrinsic-width cluster hosted in the top bar's
   *                centre slot; connectors are a fixed short length so the
   *                stepper doesn't stretch edge-to-edge.
   * Driven by `usePfStepperVariant()` in `StepShell`.
   */
  placement?: 'band' | 'inline'
}

export const PfProgressStepper: React.FC<Props> = ({ placement = 'band' }) => {
  const isPaymentPending = usePfStore((s) => s.paymentFailure !== null)
  const { t } = useTranslation(['purchase_flow_revamp'])
  const isMobile = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const navigate = useNavigate()
  const pfPath = usePfPath()
  const trackAnalytics = usePfRevampAnalytics()

  const { steps, currentIndex, currentStep, firstConfigTarget } = useResolvedStepFlow()

  // `currentIndex` is -1 only when the current step is not part of the
  // skip-aware sequence (impossible during normal navigation) — clamp so the
  // counter/highlight never reads "Step 0 of N".
  const safeIndex = currentIndex < 0 ? 0 : currentIndex
  const activeStep = steps[safeIndex]

  // Desktop-only: inline (in the top bar) renders a compact, intrinsic-width
  // cluster with fixed-length connectors; band stretches edge-to-edge.
  const isInline = placement === 'inline'

  /** Resolve a step's route. `configuration` needs the first configurable
   *  (cartItem, flow) tuple; returns null when it can't be built. */
  const pathForStep = useCallback((step: PfStep): string | null => {
    switch (step) {
      case 'catalogue': return pfPath(PfStepPath.CATALOGUE)
      case 'configuration':
        return firstConfigTarget
          ? pfPath(`${PfStepPath.CONFIGURATION}/${firstConfigTarget.cartItemId}/${firstConfigTarget.flowId}`)
          : null
      case 'instructions': return pfPath(PfStepPath.INSTRUCTIONS)
      case 'billing': return pfPath(PfStepPath.BILLING)
      case 'summary': return pfPath(PfStepPath.SUMMARY)
    }
  }, [pfPath, firstConfigTarget])

  /** Jump back to an already-completed step (forward steps stay locked). */
  const goToStep = useCallback((step: PfStep) => {
    const path = pathForStep(step)
    if (!path) return
    navigate(path)
    trackAnalytics({
      type: PfRevampEventName.STEP_RETURNED,
      payload: { fromStep: currentStep, toStep: step },
    })
  }, [pathForStep, navigate, trackAnalytics, currentStep])

  return (
    <Box
      sx={{ width: isInline ? 'auto' : '100%', maxWidth: '100%' }}
      data-cy="pf-revamp-progress-stepper"
      data-pf-stepper-placement={placement}
    >

      {!isMobile &&
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={isInline ? 'center' : 'flex-start'}
          sx={{ width: isInline ? 'auto' : '100%' }}
        >
          {steps.map((step, index) => {
            const status = statusFor(index, safeIndex)
            const isLast = index === steps.length - 1
            const isClickable = status === 'completed' && !isPaymentPending
            const badgeBg = status === 'completed' ? GREEN_600 : status === 'active' ? CORAL_600 : 'transparent'

            const stepContent = (
              <Stack direction="row" alignItems="center" gap={0.8} sx={{ flexShrink: 0 }}>

                <Box
                  sx={{
                    width: '2rem',
                    height: '2rem',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    backgroundColor: badgeBg,
                    border: status === 'upcoming' ? `1px solid ${GRAY_400}` : 'none',
                    color: status === 'upcoming' ? GRAY_500 : WHITE,
                  }}
                >
                  {status === 'completed'
                    ? <CheckRoundedIcon sx={{ fontSize: '1.4rem' }} />
                    : <Typography variant="text-xs" fontWeight={600} color="inherit">{index + 1}</Typography>}
                </Box>

                <Typography
                  variant="text-sm"
                  fontWeight={status === 'active' ? 600 : 400}
                  color={status === 'upcoming' ? GRAY_500 : GRAY_900}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t(STEP_LABEL_KEY[step])}
                </Typography>

              </Stack>
            )

            return (
              <React.Fragment key={step}>

                {isClickable
                  ? <ButtonBase
                    onClick={() => goToStep(step)}
                    aria-label={t('purchase_flow_revamp:stepper.go_to_step', { step: t(STEP_LABEL_KEY[step]) })}
                    sx={{ borderRadius: '0.4rem', flexShrink: 0, '&:hover .MuiTypography-root': { textDecoration: 'underline' } }}
                  >
                    {stepContent}
                  </ButtonBase>
                  : stepContent}

                {!isLast &&
                  <Box
                    sx={{
                      flex: isInline ? '0 0 auto' : 1,
                      width: isInline ? '2.4rem' : 'auto',
                      minWidth: isInline ? '1.6rem' : undefined,
                      height: '0.2rem',
                      mx: isInline ? 0.8 : 1.2,
                      borderRadius: '0.1rem',
                      backgroundColor: index < safeIndex ? GREEN_600 : GRAY_400,
                    }}
                  />
                }

              </React.Fragment>
            )
          })}
        </Stack>
      }

      {isMobile &&
        <Stack
          sx={{ width: '100%' }}
          role="img"
          aria-label={`${activeStep ? t(STEP_LABEL_KEY[activeStep]) : ''}, ${t('purchase_flow_revamp:stepper.step_counter', { current: safeIndex + 1, total: steps.length })}`}
        >

          <Stack direction="row" gap={0.6} sx={{ width: '100%' }}>
            {steps.map((step, index) => {
              const status = statusFor(index, safeIndex)
              const segmentColor = status === 'completed' ? GREEN_600 : status === 'active' ? GRAY_900 : GRAY_400

              return (
                <Box
                  key={step}
                  sx={{
                    flex: 1,
                    height: '0.4rem',
                    borderRadius: '0.2rem',
                    backgroundColor: segmentColor,
                  }}
                />
              )
            })}
          </Stack>

        </Stack>
      }

    </Box>
  )
}
